import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { dashboard, sms } from "../lib/api";
import { timeAgo } from "../lib/time";
import { Card } from "../components/ui/card";
import SemaforoBadge from "../components/SemaforoBadge";
import MyFindingsWidget from "../components/MyFindingsWidget";
import ActivityChartCard from "../components/ActivityChartCard";
import { localizedTemplate, localizedCategory } from "../lib/i18nContent";
import {
  ClipboardList,
  Store as StoreIcon,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Eye,
  EyeOff,
  Share2,
  BarChart3,
  Trophy,
  Inbox,
  LayoutDashboard,
  RotateCcw,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { useApp } from "../context/AppContext";

const SECTION_KEYS = [
  { key: "kpis", icon: LayoutDashboard, color: "#0B1220" },
  { key: "activity", icon: TrendingUp, color: "#0EA5E9" },
  { key: "trend", icon: TrendingUp, color: "#1E3A8A" },
  { key: "semaphore", icon: BarChart3, color: "#10B981" },
  { key: "ranking", icon: Trophy, color: "#D97706" },
  { key: "recent", icon: AlertCircle, color: "#EF4444" },
  { key: "my_findings", icon: Inbox, color: "#7C3AED" },
  { key: "share", icon: Eye, color: "#1E3A8A" },
];
const DEFAULT_SECTIONS = SECTION_KEYS.reduce((a, s) => ({ ...a, [s.key]: true }), {});
const STORAGE_KEY = "canales.dashboard.sections.v1";

function loadSectionsPref() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SECTIONS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SECTIONS, ...parsed };
  } catch {
    return DEFAULT_SECTIONS;
  }
}

function SectionTogglesBar({ sections, onToggle, onShowAll, onHideAll, onReset }) {
  const { t } = useTranslation();
  const visibleCount = SECTION_KEYS.filter((s) => sections[s.key]).length;
  return (
    <Card className="canales-card p-4 mt-5" data-testid="dashboard-sections-bar">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="canales-overline flex items-center gap-1.5">
            <LayoutDashboard size={11} /> {t("dashboard_sections.title")}
          </div>
          <p className="text-xs text-stone-500 mt-1 max-w-md">
            {t("dashboard_sections.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="pill bg-stone-100 text-stone-700 text-[10px]">
            {visibleCount}/{SECTION_KEYS.length} · {visibleCount === SECTION_KEYS.length ? t("dashboard_sections.all_visible") : t("dashboard_sections.hidden")}
          </span>
          <button
            type="button"
            onClick={onShowAll}
            className="text-[11px] text-stone-600 hover:text-[#1E3A8A] underline"
            data-testid="sections-show-all"
          >
            {t("dashboard_sections.show_all")}
          </button>
          <button
            type="button"
            onClick={onHideAll}
            className="text-[11px] text-stone-600 hover:text-[#1E3A8A] underline"
            data-testid="sections-hide-all"
          >
            {t("dashboard_sections.hide_all")}
          </button>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] text-stone-600 hover:text-[#1E3A8A] underline inline-flex items-center gap-1"
            data-testid="sections-reset"
          >
            <RotateCcw size={10} /> {t("dashboard_sections.reset")}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {SECTION_KEYS.map(({ key, icon: Icon, color }) => {
          const visible = !!sections[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key)}
              className={`group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                visible
                  ? "bg-white border-stone-200 hover:border-stone-300 shadow-sm"
                  : "bg-stone-100 border-stone-200 text-stone-500 hover:bg-stone-200"
              }`}
              data-testid={`section-toggle-${key}`}
              aria-pressed={visible}
            >
              <span
                className="w-5 h-5 rounded-md grid place-items-center"
                style={{
                  background: visible ? `${color}1A` : "#E5E5E5",
                  color: visible ? color : "#A3A3A3",
                }}
              >
                <Icon size={11} />
              </span>
              <span>{t(`dashboard_sections.${key}`)}</span>
              {visible ? (
                <Eye size={11} className="text-stone-400 group-hover:text-stone-600" />
              ) : (
                <EyeOff size={11} className="text-stone-400" />
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

const KPI = ({ label, value, icon: Icon, hint, accent = "#0B1220", testid }) => (
  <div className="canales-card p-5 fade-up" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="canales-overline">{label}</div>
      <span className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: `${accent}10`, color: accent }}>
        <Icon size={14} />
      </span>
    </div>
    <div className="font-display text-3xl mt-3" style={{ color: accent }}>
      {value}
    </div>
    {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
  </div>
);

export default function DashboardPage() {
  const { user, viewAllStores, can } = useApp();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [sections, setSections] = useState(() => loadSectionsPref());

  useEffect(() => {
    dashboard.get(viewAllStores).then(setData).catch(console.error);
  }, [viewAllStores]);

  // Fire-and-forget: scan for TO DOs >24h without follow-up at most once per
  // hour per browser session. The backend itself is idempotent (writes
  // `reminder_sent_at`), but we throttle here to avoid spamming the endpoint.
  useEffect(() => {
    const KEY = "canales.last_24h_scan_at";
    try {
      const last = Number(localStorage.getItem(KEY) || 0);
      if (Date.now() - last < 60 * 60 * 1000) return;
      localStorage.setItem(KEY, String(Date.now()));
      sms.scanNoFollowup().catch(() => { /* fire-and-forget */ });
    } catch { /* ignore storage errors */ }
  }, []);

  const persist = (next) => {
    setSections(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  const toggleSection = (key) => persist({ ...sections, [key]: !sections[key] });
  const showAll = () => persist(SECTION_KEYS.reduce((a, s) => ({ ...a, [s.key]: true }), {}));
  const hideAll = () => persist(SECTION_KEYS.reduce((a, s) => ({ ...a, [s.key]: false }), {}));
  const reset = () => persist(DEFAULT_SECTIONS);

  if (!data) {
    return <div className="p-10 text-stone-500">{t("common.loading_dashboard")}</div>;
  }

  const { kpis, semaphore, ranking, trend, recent_findings, share_activity = [] } = data;

  const semaphoreData = [
    { name: "Verde (≥85)", value: semaphore.verde, color: "#10B981" },
    { name: "Amarillo (70-84)", value: semaphore.amarillo, color: "#F59E0B" },
    { name: "Rojo (<70)", value: semaphore.rojo, color: "#EF4444" },
  ];

  return (
    <div data-testid="dashboard-page">
      {/* Hero */}
      <section className="hero-night text-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mt-2">
            <div>
              <h1 className="font-display text-4xl md:text-6xl leading-[1.05]">
                {t("dashboard.greeting", { name: user?.name?.split(" ")[0] || "" })}
                <br />
                <span className="bg-gradient-to-r from-[#FACC15] via-[#2E4DAA] to-[#10B981] bg-clip-text text-transparent">
                  {user?.role === "gerente" ? t("dashboard.subtitle_mine") : t("dashboard.subtitle_global")}
                </span>
              </h1>
              <p className="text-stone-300 mt-4 max-w-xl">
                {kpis.total_audits === 0
                  ? t("dashboard.no_audits")
                  : t("dashboard.hero_paragraph")}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/stores" className="brand-button px-5 h-10 inline-flex items-center" data-testid="dashboard-go-stores">
                {t("dashboard.start_audit")} <ArrowRight size={14} className="ml-1.5" />
              </Link>
              <Link to="/audits" className="brand-button-ghost px-5 h-10 inline-flex items-center bg-white/10 text-white border-white/15 hover:bg-white/20">
                {t("dashboard.view_history")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-8">
        {/* Section toggles */}
        <SectionTogglesBar
          sections={sections}
          onToggle={toggleSection}
          onShowAll={showAll}
          onHideAll={hideAll}
          onReset={reset}
        />

        {/* KPIs */}
        {sections.kpis && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3 mt-5" data-testid="section-kpis">
            <KPI label={t("dashboard.kpi_audits")} value={kpis.total_audits} icon={ClipboardList} testid="kpi-audits" />
            <KPI label={t("dashboard.kpi_closed")} value={kpis.closed_audits} icon={CheckCircle2} accent="#10B981" testid="kpi-closed" />
            <KPI label={t("dashboard.kpi_in_progress")} value={kpis.in_progress} icon={ClipboardList} accent="#1E3A8A" testid="kpi-in-progress" />
            <KPI label={t("dashboard.kpi_avg")} value={`${kpis.avg_score || 0}%`} icon={TrendingUp} testid="kpi-avg-score" />
            <KPI label={t("dashboard.kpi_stores")} value={kpis.total_stores} icon={StoreIcon} testid="kpi-stores" />
            <KPI label={t("dashboard.kpi_findings")} value={kpis.open_findings} icon={AlertTriangle} accent="#EF4444" hint={`${kpis.high_severity_findings} ${t("findings.title").toLowerCase()}`} testid="kpi-open-findings" />
            <KPI label={t("dashboard.kpi_views")} value={kpis.total_share_views || 0} icon={Eye} accent="#1E3A8A" testid="kpi-share-views" />
          </div>
        )}
        {sections.activity && <ActivityChartCard viewAll={viewAllStores} />}
        {(sections.trend || sections.semaphore) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
            {sections.trend && (
              <Card className={`canales-card p-6 ${sections.semaphore ? "lg:col-span-2" : "lg:col-span-3"}`} data-testid="section-trend">
            <div className="flex items-center justify-between">
              <div>
                <div className="canales-overline">{t("dashboard.trend")}</div>
                <h3 className="font-display text-xl mt-1">{t("dashboard.trend_subtitle")}</h3>
              </div>
              <Sparkles size={16} className="text-stone-300" />
            </div>
            <div className="h-72 mt-4">
              {trend.length === 0 ? (
                <div className="h-full flex items-center justify-center text-stone-400 text-sm">
                  {t("common.empty")}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} domain={[0, 100]} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB" }} />
                    <Line type="monotone" dataKey="avg" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
            )}

            {sections.semaphore && (
              <Card className={`canales-card p-6 ${sections.trend ? "" : "lg:col-span-3"}`} data-testid="section-semaphore">
                <div className="canales-overline">{t("dashboard.semaphore")}</div>
                <h3 className="font-display text-xl mt-1">{t("dashboard.semaphore_subtitle")}</h3>
                <div className="h-72 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={semaphoreData} layout="vertical" margin={{ left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                      <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={120} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB" }} />
                      <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                        {semaphoreData.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
            )}
          </div>
        )}

        {(sections.ranking || sections.recent) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
            {sections.ranking && (
              <Card className={`canales-card p-6 ${sections.recent ? "" : "lg:col-span-2"}`} data-testid="section-ranking">
                <div className="canales-overline">{t("dashboard.ranking")}</div>
                <h3 className="font-display text-xl mt-1">{t("dashboard.ranking_subtitle")}</h3>
                <div className="mt-4">
                  {ranking.length === 0 ? (
                    <div className="text-sm text-stone-500 py-8 text-center">{t("common.empty")}</div>
                  ) : (
                    <ul className="divide-y divide-stone-100" data-testid="ranking-list">
                      {ranking.map((r, i) => (
                        <li key={i} className="py-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <span className={`w-7 h-7 rounded-full text-xs flex items-center justify-center font-bold shrink-0 ${
                              i === 0 ? "bg-[#1E3A8A] text-white" : "bg-stone-100 text-stone-700"
                            }`}>
                              {i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm truncate">{r.store}</div>
                              <div className="text-xs text-stone-500 flex items-center gap-1.5 flex-wrap">
                                <span>{r.distrito || "—"}</span>
                                {(r.templates || []).slice(0, 3).map((tpl) => (
                                  <span key={tpl} className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#E5EBFA] text-[#1E3A8A]">
                                    {localizedTemplate(tpl, i18n.language)}
                                  </span>
                                ))}
                                {(r.templates || []).length > 3 && (
                                  <span className="text-[10px] text-stone-400">+{r.templates.length - 3}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <SemaforoBadge score={r.score} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            )}

            {sections.recent && (
              <Card className={`canales-card p-6 ${sections.ranking ? "" : "lg:col-span-2"}`} data-testid="section-recent">
            <div className="canales-overline">{t("dashboard.recent_findings")}</div>
            <h3 className="font-display text-xl mt-1">{t("dashboard.recent_findings_subtitle")}</h3>
            <div className="mt-4">
              {recent_findings.length === 0 ? (
                <div className="text-sm text-stone-500 py-8 text-center">{t("findings.no_findings")}</div>
              ) : (
                <ul className="space-y-2" data-testid="recent-findings">
                  {recent_findings.slice(0, 6).map((f) => (
                    <li key={f.id} className="flex items-start gap-3 p-3 rounded-xl bg-stone-50">
                      <AlertCircle
                        size={16}
                        className={
                          f.severity === "Alta"
                            ? "text-red-600 mt-0.5"
                            : f.severity === "Media"
                            ? "text-amber-600 mt-0.5"
                            : "text-stone-500 mt-0.5"
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{f.description}</div>
                        <div className="text-xs text-stone-500 mt-0.5">
                          {f.store_name} · {localizedCategory(f.category, i18n.language)} · {t(`finding_status.${f.status}`, f.status)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
              </Card>
            )}
          </div>
        )}

        {/* My assigned findings widget */}
        {sections.my_findings && can("view_my_findings") && (
          <div className="mt-5" data-testid="section-my-findings">
            <MyFindingsWidget />
          </div>
        )}

        {/* Share activity widget */}
        {sections.share && (
        <Card className="canales-card p-6 mt-5" data-testid="share-activity-card">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="canales-overline flex items-center gap-1.5">
                <Share2 size={11} /> {t("dashboard.kpi_views")}
              </div>
              <h3 className="font-display text-xl mt-1">{t("dashboard.share_activity_title")}</h3>
              <p className="text-xs text-stone-500 mt-1">
                {t("dashboard.share_activity_subtitle")}
              </p>
            </div>
            <div className="pill bg-[#E5EBFA] text-[#1E3A8A]">
              <Eye size={12} /> {kpis.total_share_views || 0} {t("summary.views").toLowerCase()}
            </div>
          </div>

          {share_activity.length === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-stone-200 p-8 text-center">
              <Eye size={24} className="mx-auto text-stone-300" />
              <p className="text-sm text-stone-500 mt-3">
                {t("dashboard.share_empty")}
              </p>
            </div>
          ) : (
            <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="share-activity-list">
              {share_activity.map((s) => (
                <li
                  key={s.audit_id}
                  className="rounded-xl border border-stone-200 p-4 hover:border-[#1E3A8A] hover:bg-[#E5EBFA]/30 transition fade-up"
                  data-testid={`share-activity-${s.audit_id}`}
                >
                  <Link to={`/summary/${s.audit_id}`} className="block">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-8 h-8 rounded-lg bg-[#E5EBFA] text-[#1E3A8A] grid place-items-center shrink-0">
                          <Eye size={14} />
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold truncate text-sm">{s.store_name}</div>
                          <div className="text-xs text-stone-500">
                            Visto {timeAgo(s.share_last_viewed_at)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display text-lg leading-none">{s.share_views}</div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">vistas</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      {s.score > 0 ? (
                        <SemaforoBadge score={s.score} />
                      ) : (
                        <span className="pill text-[10px]">En curso</span>
                      )}
                      <span className="text-[11px] text-[#1E3A8A] font-semibold inline-flex items-center gap-1">
                        Ver reporte <ArrowRight size={11} />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        )}
      </div>
    </div>
  );
}
